import { Capacitor, registerPlugin } from '@capacitor/core';
import { InMemoryWebStorage, UserManager, WebStorageStateStore } from 'oidc-client-ts';
import type { ShellConfig } from './types';

/**
 * Inside the A.C.C.R.A iPhone app (1 November 2026, Matthew: "I shouldn't
 * have to keep logging in", 30 days with Face ID) the app signs in
 * natively with its own Cognito client and hands this page the ID token
 * (accra-app AccraAuthPlugin.swift). The refresh token stays in the iPhone
 * Keychain behind Face ID and never reaches this page; the ID token is
 * held in memory only, as in the browser. An older app build without the
 * plugin keeps the web sign-in below.
 */
interface NativeSession {
  idToken: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}
interface AccraAuthPlugin {
  session(): Promise<NativeSession>;
  signOut(): Promise<void>;
}
const AccraAuth = registerPlugin<AccraAuthPlugin>('AccraAuth');

export function nativeSignIn(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AccraAuth');
}

/** Renew this long before the ID token expires. */
export const RENEW_BEFORE_MS = 5 * 60 * 1000;

export function needsRenewal(session: NativeSession, now = Date.now()): boolean {
  return session.expiresAt - now < RENEW_BEFORE_MS;
}

async function startNativeAuth(): Promise<AuthSession> {
  // Resolves once the app has signed in or unlocked with Face ID.
  let current = await AccraAuth.session();
  let renewing: Promise<void> | null = null;
  const renew = () => {
    renewing ??= AccraAuth.session()
      .then((s) => {
        current = s;
      })
      .catch(() => undefined)
      .finally(() => {
        renewing = null;
      });
    return renewing;
  };
  window.setInterval(() => {
    if (needsRenewal(current)) void renew();
  }, 30 * 1000);
  // Back from the background: the token may have run out meanwhile.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && needsRenewal(current)) void renew();
  });
  return {
    getAccessToken: () => current.idToken,
    signOut: () => void AccraAuth.signOut().then(() => window.location.reload()),
  };
}

/**
 * Authentication (spec §2.1.2), Cognito via OIDC code flow with PKCE.
 *
 * Storage: tokens live strictly in memory. No access token, id token or
 * refresh token is ever written to localStorage, sessionStorage or
 * IndexedDB; that is a hard architectural rule. A page refresh drops them
 * and round-trips through Cognito, whose own session cookie makes that hop
 * invisible while it is valid.
 *
 * The single exception is the transient OIDC state store below, and it is
 * forced by the flow rather than chosen. See the note on stateStore.
 *
 * Session policy: access tokens live 1 hour and renew silently on the
 * refresh token. The 8 hour sliding TTL is enforced here by the idle
 * timer (any interaction slides it); the 24 hour absolute cap is the
 * refresh token validity configured on the user pool client, which no
 * client code can extend. MFA (TOTP) is enforced by the pool, not by
 * this app.
 */

export const IDLE_TIMEOUT_MS = 8 * 60 * 60 * 1000;
const IDLE_CHECK_INTERVAL_MS = 60 * 1000;

export interface AuthSession {
  getAccessToken(): string | null;
  signOut(): void;
}

export async function startAuth(config: ShellConfig): Promise<AuthSession | null> {
  if (nativeSignIn() && !config.devAuth) return startNativeAuth();
  if (config.devAuth) {
    return { getAccessToken: () => null, signOut: () => window.location.reload() };
  }
  if (!config.cognito) {
    throw new Error('config.json has neither devAuth nor cognito settings');
  }

  const manager = new UserManager({
    authority: config.cognito.authority,
    client_id: config.cognito.clientId,
    redirect_uri: `${window.location.origin}/auth/callback`,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    scope: 'openid email',
    // Tokens: memory only, dropped on refresh. The hard rule.
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
    // Transient auth state: sessionStorage, and it has to be. signinRedirect
    // navigates the whole page to Cognito, which destroys the heap, so an
    // in-memory state store is always empty by the time the callback runs
    // and every sign in dies on "No matching state found in storage". What
    // lives here is a random state id and a single-use PKCE code_verifier,
    // for the few seconds between redirect and callback; oidc-client-ts
    // deletes the entry once it is consumed. No token is ever written here,
    // it is scoped to the one tab, and it is never sent to a server. The
    // only way back to zero storage is a popup flow, which trades this for
    // popup blockers on an operations surface.
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
  });

  if (window.location.pathname === '/auth/callback') {
    // Put the fragment back. A URL fragment is never sent to a server, so
    // #board does not reach Cognito and does not come back on the
    // redirect_uri: opening a bookmarked view while signed out used to
    // land on Home with the hash stripped from the address bar, leaving
    // nothing for anyone to notice or recover (14 September 2026).
    //
    // The OIDC `state` parameter is exactly the mechanism for this and it
    // round-trips through the authorization server, so nothing new is
    // stored here. Validated before use: it comes back through a redirect
    // and a fragment is written straight into the address bar, so it is
    // matched against the shape the app writes rather than trusted.
    const returned = await manager.signinCallback();
    const wanted = (returned?.state as { returnTo?: string } | undefined)?.returnTo;
    const safe = typeof wanted === 'string' && /^#[a-z0-9/-]{1,80}$/i.test(wanted) ? wanted : '';
    window.history.replaceState(null, '', `/${safe}`);
  }

  let user = await manager.getUser();
  if (!user || user.expired) {
    // Where they were going, carried across the sign-in.
    await manager.signinRedirect({ state: { returnTo: window.location.hash } });
    return null; // navigating away
  }

  manager.events.addUserLoaded((u) => {
    user = u;
  });
  manager.events.addSilentRenewError(() => {
    void manager.signinRedirect();
  });
  manager.events.addAccessTokenExpired(() => {
    void manager.signinRedirect();
  });

  // 8 hour sliding idle timeout. Activity slides it; expiry signs out.
  let lastActivity = Date.now();
  const slide = () => {
    lastActivity = Date.now();
  };
  for (const eventName of ['pointerdown', 'keydown', 'visibilitychange'] as const) {
    window.addEventListener(eventName, slide, { passive: true });
  }
  window.setInterval(() => {
    if (Date.now() - lastActivity > IDLE_TIMEOUT_MS) {
      void manager.signoutRedirect();
    }
  }, IDLE_CHECK_INTERVAL_MS);

  return {
    // The ID token is the API credential: the HTTP API's JWT authorizer
    // validates its aud against the client id, and it is the token that
    // carries cognito:groups, email and custom:brands, which the RBAC
    // layer needs. Cognito access tokens omit custom attributes entirely.
    getAccessToken: () => user?.id_token ?? null,
    signOut: () => void manager.signoutRedirect(),
  };
}
